/* eslint-disable react-hooks/exhaustive-deps */
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import EventNoteIcon from "@mui/icons-material/EventNote";
import GroupIcon from "@mui/icons-material/Group";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LocalPrintshopIcon from "@mui/icons-material/LocalPrintshop";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { esArray } from "src/general_components/functionEsArray";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import { PageHeaderCard } from "src/general_components/PageHeaderCard";
import { SnackbarMessenger } from "src/general_components/SnackbarMessenger";
import { getCuentasJuridicas } from "src/projects/gestion_comercial/helpers/CuentasJuridicasData";
import { procesarServicioGuarda } from "../helpers/ServiciosGuardaData";

// ─── Pasos "cómo funciona" ──────────────────────────────────────────────────
const PASOS = [
  {
    icon: <GroupIcon fontSize="small" color="primary" />,
    titulo: "Seleccionar clientes",
    descripcion: "Elegí uno o más clientes para los que se generarán los comprobantes.",
  },
  {
    icon: <EventNoteIcon fontSize="small" color="primary" />,
    titulo: "Definir período",
    descripcion: "Indicá el rango de fechas que cubre el servicio de guarda.",
  },
  {
    icon: <PlayCircleOutlineIcon fontSize="small" color="primary" />,
    titulo: "Confirmar y procesar",
    descripcion: "Revisá el resumen y confirmá. El sistema contará las cajas activas por tipo.",
  },
  {
    icon: <DescriptionOutlinedIcon fontSize="small" color="primary" />,
    titulo: "Comprobantes generados",
    descripcion: "Se generará un comprobante CPBI por cliente con los ítems correspondientes.",
  },
];

export function ServiciosGuarda() {
  const [clienteOptions, setClienteOptions] = useState([]);
  const [clientesSeleccionados, setClientesSeleccionados] = useState([]);
  const [fechaDesde, setFechaDesde] = useState(dayjs().startOf("month"));
  const [fechaHasta, setFechaHasta] = useState(dayjs());

  const [clientesError, setClientesError] = useState(false);
  const [fechaDesdeError, setFechaDesdeError] = useState(false);
  const [fechaHastaError, setFechaHastaError] = useState(false);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [procesando, setProcesando] = useState(false);

  // Resultados: array de { cliente, id_comprobante, error }
  const [resultados, setResultados] = useState(null);

  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });

  useEffect(() => {
    const cargarClientes = async () => {
      const res = esArray(await getCuentasJuridicas());
      const opciones = res.map((c) => ({
        ...c,
        label: c.id_cuentas + " - " + c.razon_social,
        value: c.id_cuentas,
      }));
      setClienteOptions(opciones);
    };
    cargarClientes();
  }, []);

  // Listener para acciones de la IA
  useEffect(() => {
    const imprimirComprobante = (id_comprobante) => {
      const credentials = JSON.parse(localStorage.getItem("clientData"));
      if (!credentials || !id_comprobante) return;
      const { dominio, owner_code } = credentials;
      const url = `${dominio.replace(/\/+$/, "")}/comprobantes/getComprobanteView/${id_comprobante}?owner_code=${owner_code}`;
      window.open(url, "_blank");
    };

    const handler = async (e) => {
      const { type, params } = e.detail || {};

      // ── Imprimir comprobante ────────────────────────────────────────────
      if (type === "servicios_guarda_imprimir") {
        imprimirComprobante(params?.id_comprobante);
        return;
      }

      if (type !== "servicios_guarda_generar" || !params) return;

      const { clientes, fecha_desde, fecha_hasta } = params;
      if (!clientes?.length || !fecha_desde || !fecha_hasta) return;

      // Construir opciones de cliente compatibles con el Autocomplete
      const clienteOpts = clientes.map((c) => ({
        ...c,
        label: c.id_cuentas + " - " + c.razon_social,
        value: c.id_cuentas,
      }));

      // Actualizar UI
      setClientesSeleccionados(clienteOpts);
      setFechaDesde(dayjs(fecha_desde));
      setFechaHasta(dayjs(fecha_hasta));
      setClientesError(false);
      setFechaDesdeError(false);
      setFechaHastaError(false);
      setResultados(null);
      setProcesando(true);

      try {
        const response = await procesarServicioGuarda({
          id_cuentas: clientes.map((c) => c.id_cuentas),
          fecha_desde,
          fecha_hasta,
        });

        if (response?.status === "true") {
          const idsStr = response?.wsResponse?.comprobantes_generados ?? "";
          const ids = idsStr
            ? String(idsStr).split(",").map((id) => parseInt(id.trim(), 10))
            : [];
          const filas = clienteOpts.map((cliente, i) => ({
            cliente: cliente.label,
            id_comprobante: ids[i] ?? null,
            error: null,
          }));
          setResultados(filas);
          // Compartir resultados con el widget de IA para poder imprimir por chat
          window.dispatchEvent(new CustomEvent("ai:state", { detail: { serviciosGuardaResultados: filas } }));
          setSnackbar({
            open: true,
            message: `Se generaron ${ids.length} comprobante(s) exitosamente.`,
            severity: "success",
          });
          setClientesSeleccionados([]);
        } else {
          setResultados([{ cliente: "—", id_comprobante: null, error: response?.message || "Error desconocido" }]);
          setSnackbar({
            open: true,
            message: response?.message || "Ocurrió un error al procesar.",
            severity: "error",
          });
        }
      } catch {
        setResultados([{ cliente: "—", id_comprobante: null, error: "Error inesperado en el servidor." }]);
        setSnackbar({ open: true, message: "Error inesperado en el servidor.", severity: "error" });
      } finally {
        setProcesando(false);
      }
    };

    window.addEventListener("ai:action", handler);
    return () => window.removeEventListener("ai:action", handler);
  }, []);

  const validar = () => {
    let ok = true;
    if (!clientesSeleccionados.length) { setClientesError(true); ok = false; }
    if (!fechaDesde || !fechaDesde.isValid()) { setFechaDesdeError(true); ok = false; }
    if (!fechaHasta || !fechaHasta.isValid()) { setFechaHastaError(true); ok = false; }
    if (fechaDesde && fechaHasta && fechaDesde.isAfter(fechaHasta)) {
      setFechaDesdeError(true);
      setFechaHastaError(true);
      ok = false;
    }
    return ok;
  };

  const handleProcesar = () => {
    if (!validar()) return;
    setConfirmOpen(true);
  };

  const handleConfirmar = async () => {
    setConfirmOpen(false);
    setProcesando(true);
    setResultados(null);

    try {
      const response = await procesarServicioGuarda({
        id_cuentas: clientesSeleccionados.map((c) => c.value),
        fecha_desde: fechaDesde.format("YYYY-MM-DD"),
        fecha_hasta: fechaHasta.format("YYYY-MM-DD"),
      });

      if (response?.status === "true") {
        // Parsear IDs generados: "71,72,73" → [71, 72, 73]
        const idsStr = response?.wsResponse?.comprobantes_generados ?? "";
        const ids = idsStr
          ? String(idsStr).split(",").map((id) => parseInt(id.trim(), 10))
          : [];

        const filas = clientesSeleccionados.map((cliente, i) => ({
          cliente: cliente.label,
          id_comprobante: ids[i] ?? null,
          error: null,
        }));

        setResultados(filas);
        window.dispatchEvent(new CustomEvent("ai:state", { detail: { serviciosGuardaResultados: filas } }));
        setSnackbar({
          open: true,
          message: `Se generaron ${ids.length} comprobante(s) exitosamente.`,
          severity: "success",
        });
        setClientesSeleccionados([]);
      } else {
        setResultados([{ cliente: "—", id_comprobante: null, error: response?.message || "Error desconocido" }]);
        setSnackbar({
          open: true,
          message: response?.message || "Ocurrió un error al procesar.",
          severity: "error",
        });
      }
    } catch {
      setResultados([{ cliente: "—", id_comprobante: null, error: "Error inesperado en el servidor." }]);
      setSnackbar({ open: true, message: "Error inesperado en el servidor.", severity: "error" });
    } finally {
      setProcesando(false);
    }
  };

  const handleNuevaConsulta = () => {
    setResultados(null);
    setClientesSeleccionados([]);
    setFechaDesde(dayjs().startOf("month"));
    setFechaHasta(dayjs());
  };

  const exitoTotal = resultados && resultados.every((r) => !r.error);
  const cantGenerados = resultados ? resultados.filter((r) => r.id_comprobante).length : 0;

  const cleanDomain = (domain) =>
    typeof domain === "string" ? domain.replace(/\/+$/, "") : "";

  const handleImprimir = (id_comprobante, view) => {
    const credentials = JSON.parse(localStorage.getItem("clientData"));
    if (!credentials || !id_comprobante) return;
    const { dominio, owner_code } = credentials;
    const viewParam = view === "full" ? "&view=full" : "";
    const url = `${cleanDomain(dominio)}/comprobantes/getComprobanteView/${id_comprobante}?owner_code=${owner_code}${viewParam}`;
    window.open(url, "_blank");
  };

  return (
    <Grid style={{ marginBottom: "50px", paddingTop: 10 }}>
      <PageHeaderCard title="Servicios de Guarda" icon={<Inventory2Icon />} />

      {/* ── Cómo funciona ── */}
      <Card elevation={2} sx={{ mt: 2, mb: 2 }}>
        <CardContent sx={{ pb: "12px !important" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
            <InfoOutlinedIcon color="primary" fontSize="small" />
            <Typography variant="subtitle2" color="primary" fontWeight={600}>
              ¿Cómo funciona?
            </Typography>
          </Box>
          <Grid container spacing={2}>
            {PASOS.map((paso, i) => (
              <Grid item xs={12} sm={6} md={3} key={i}>
                <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
                  <Box
                    sx={{
                      minWidth: 28, height: 28,
                      borderRadius: "50%",
                      bgcolor: "primary.main",
                      color: "white",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 13, fontWeight: 700, flexShrink: 0,
                    }}
                  >
                    {i + 1}
                  </Box>
                  <Box>
                    <Typography variant="body2" fontWeight={600}>{paso.titulo}</Typography>
                    <Typography variant="caption" color="text.secondary">{paso.descripcion}</Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      {/* ── Formulario ── */}
      <Card elevation={3}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
            Generar comprobantes de servicio de guarda
          </Typography>
          <Divider sx={{ mb: 3 }} />

          {procesando && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

          <Grid container spacing={2}>
            {/* Clientes */}
            <Grid item xs={12}>
              <Autocomplete
                multiple
                options={clienteOptions}
                value={clientesSeleccionados}
                onChange={(_, value) => {
                  setClientesSeleccionados(value);
                  setClientesError(false);
                }}
                getOptionLabel={(o) => o.label ?? ""}
                isOptionEqualToValue={(o, v) => o.value === v.value}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      key={option.value}
                      label={option.label}
                      size="small"
                      color="primary"
                      variant="outlined"
                      {...getTagProps({ index })}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Clientes *"
                    error={clientesError}
                    helperText={
                      clientesError
                        ? "Seleccione al menos un cliente"
                        : `${clientesSeleccionados.length} cliente(s) seleccionado(s)`
                    }
                    size="small"
                  />
                )}
              />
            </Grid>

            {/* Fechas */}
            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Fecha desde *"
                  inputFormat="DD/MM/YYYY"
                  value={fechaDesde}
                  onChange={(val) => {
                    setFechaDesde(val);
                    setFechaDesdeError(false);
                    setFechaHastaError(false);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      fullWidth
                      error={fechaDesdeError}
                      helperText={fechaDesdeError ? "Fecha inválida o mayor a 'hasta'" : "Inicio del período de guarda"}
                    />
                  )}
                />
              </LocalizationProvider>
            </Grid>

            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Fecha hasta *"
                  inputFormat="DD/MM/YYYY"
                  value={fechaHasta}
                  onChange={(val) => {
                    setFechaHasta(val);
                    setFechaDesdeError(false);
                    setFechaHastaError(false);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      fullWidth
                      error={fechaHastaError}
                      helperText={fechaHastaError ? "Fecha inválida o menor a 'desde'" : "Fin del período de guarda"}
                    />
                  )}
                />
              </LocalizationProvider>
            </Grid>

            {/* Resumen rápido */}
            {clientesSeleccionados.length > 0 && fechaDesde?.isValid() && fechaHasta?.isValid() && !fechaDesde.isAfter(fechaHasta) && (
              <Grid item xs={12}>
                <Alert severity="info" icon={<InfoOutlinedIcon />} sx={{ py: 0.5 }}>
                  Se generarán <strong>{clientesSeleccionados.length}</strong> comprobante(s) para el período{" "}
                  <strong>{fechaDesde.format("DD/MM/YYYY")}</strong> —{" "}
                  <strong>{fechaHasta.format("DD/MM/YYYY")}</strong>.
                </Alert>
              </Grid>
            )}

            {/* Acciones */}
            <Grid item xs={12} sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
              {resultados && (
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={handleNuevaConsulta}
                  disabled={procesando}
                >
                  Nueva consulta
                </Button>
              )}
              <Tooltip title={procesando ? "Procesando, aguarde..." : ""}>
                <span>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleProcesar}
                    disabled={procesando}
                    startIcon={procesando ? <CircularProgress size={16} color="inherit" /> : <PlayCircleOutlineIcon />}
                  >
                    {procesando ? "Procesando..." : "Procesar"}
                  </Button>
                </span>
              </Tooltip>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* ── Resultados ── */}
      {resultados && (
        <Card elevation={3} sx={{ mt: 3 }}>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            {/* Encabezado resultado */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
              {exitoTotal ? (
                <CheckCircleIcon color="success" />
              ) : (
                <WarningAmberIcon color="warning" />
              )}
              <Typography variant="h6" fontWeight={600}>
                {exitoTotal
                  ? `Procesamiento completado — ${cantGenerados} comprobante(s) generado(s)`
                  : "El procesamiento tuvo errores"}
              </Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />

            {/* Resumen en chips */}
            <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap" }}>
              <Chip
                icon={<CheckCircleIcon />}
                label={`${cantGenerados} generado(s)`}
                color="success"
                size="small"
                variant="outlined"
              />
              {resultados.some((r) => r.error) && (
                <Chip
                  icon={<WarningAmberIcon />}
                  label={`${resultados.filter((r) => r.error).length} con error`}
                  color="error"
                  size="small"
                  variant="outlined"
                />
              )}
              {fechaDesde && fechaHasta && (
                <Chip
                  icon={<EventNoteIcon />}
                  label={`Período: ${fechaDesde.format("DD/MM/YYYY")} — ${fechaHasta.format("DD/MM/YYYY")}`}
                  size="small"
                  variant="outlined"
                />
              )}
            </Box>

            {/* Tabla de resultados */}
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: "grey.100" }}>
                    <TableCell sx={{ fontWeight: 700, width: 50 }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Cliente</TableCell>
                    <TableCell sx={{ fontWeight: 700, width: 180 }}>ID Comprobante</TableCell>
                    <TableCell sx={{ fontWeight: 700, width: 130 }}>Estado</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Detalle</TableCell>
                    <TableCell sx={{ fontWeight: 700, width: 220 }}>Imprimir</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {resultados.map((fila, i) => (
                    <TableRow
                      key={i}
                      hover
                      sx={{
                        bgcolor: fila.error ? "error.50" : "inherit",
                        "&:last-child td": { borderBottom: 0 },
                      }}
                    >
                      <TableCell sx={{ color: "text.secondary" }}>{i + 1}</TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {fila.cliente}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {fila.id_comprobante ? (
                          <Chip
                            label={`# ${fila.id_comprobante}`}
                            size="small"
                            color="primary"
                            sx={{ fontWeight: 700, fontSize: 13 }}
                          />
                        ) : (
                          <Typography variant="caption" color="text.disabled">—</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {fila.error ? (
                          <Chip label="Error" size="small" color="error" icon={<WarningAmberIcon />} />
                        ) : (
                          <Chip label="Generado" size="small" color="success" icon={<CheckCircleIcon />} />
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color={fila.error ? "error.main" : "text.secondary"}>
                          {fila.error ?? "Comprobante CPBI creado exitosamente"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {fila.id_comprobante && !fila.error ? (
                          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                            <Tooltip title="Imprimir con importes">
                              <Button
                                size="small"
                                variant="outlined"
                                color="info"
                                startIcon={<LocalPrintshopIcon />}
                                onClick={() => handleImprimir(fila.id_comprobante, "full")}
                              >
                                Con importes
                              </Button>
                            </Tooltip>
                            <Tooltip title="Imprimir sin importes">
                              <Button
                                size="small"
                                variant="outlined"
                                color="secondary"
                                startIcon={<LocalPrintshopIcon />}
                                onClick={() => handleImprimir(fila.id_comprobante)}
                              >
                                Sin importes
                              </Button>
                            </Tooltip>
                          </Box>
                        ) : (
                          <Typography variant="caption" color="text.disabled">—</Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Footer */}
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={handleNuevaConsulta}
                size="small"
              >
                Nueva consulta
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* ── Diálogo de confirmación ── */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <PlayCircleOutlineIcon color="primary" />
          Confirmar procesamiento
        </DialogTitle>
        <DialogContent dividers>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Esta acción generará comprobantes de servicio de guarda. Verificá los datos antes de confirmar.
          </Alert>

          <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
            Clientes seleccionados ({clientesSeleccionados.length}):
          </Typography>
          <Box
            sx={{
              maxHeight: 160,
              overflowY: "auto",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
              p: 1,
              mb: 2,
            }}
          >
            {clientesSeleccionados.map((c) => (
              <Box key={c.value} sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
                <CheckCircleIcon fontSize="inherit" color="success" />
                <Typography variant="body2">{c.label}</Typography>
              </Box>
            ))}
          </Box>

          <Divider sx={{ mb: 1.5 }} />

          <Box sx={{ display: "flex", gap: 2 }}>
            <Box>
              <Typography variant="caption" color="text.secondary">Período desde</Typography>
              <Typography variant="body2" fontWeight={600}>{fechaDesde?.format("DD/MM/YYYY")}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Período hasta</Typography>
              <Typography variant="body2" fontWeight={600}>{fechaHasta?.format("DD/MM/YYYY")}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Comprobantes a generar</Typography>
              <Typography variant="body2" fontWeight={600}>{clientesSeleccionados.length}</Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setConfirmOpen(false)} variant="outlined">
            Cancelar
          </Button>
          <Button variant="contained" color="primary" onClick={handleConfirmar} startIcon={<PlayCircleOutlineIcon />}>
            Confirmar y generar
          </Button>
        </DialogActions>
      </Dialog>

      <SnackbarMessenger
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        close={() => setSnackbar((s) => ({ ...s, open: false }))}
      />
    </Grid>
  );
}
